import { SvgIcon, Typography } from '@mui/material'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getDriver } from 'src/selectors/driver'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  StitchFetchToken,
  ContractorPortalFetchPaymentMethod,
  FetchPaymentMethod,
} from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { CancelMajor, ChevronLeftMinor } from '@shopify/polaris-icons'
import qs from 'query-string'
import _ from 'lodash'
import { StitchIframeMessage } from 'src/constants/stitch'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Button } from 'src/components/shared/Button'
import { ContractorPortalSecureWrapper } from 'src/components/layout/contractorPortal/ContractorPortalSecureWrapper'
import { useNavigate } from 'react-router-dom'
import { CONTRACTOR_PORTAL_ROUTES } from 'src/constants/routes'
import { Footer } from 'src/components/shared/Footer'
import { ContractorPortalPaymentMethod } from 'src/interfaces/contractorPortal'
import { PaymentProvider } from 'src/constants/shared'
import { DriverPaymentMethod } from 'src/interfaces/paymentMethod'
import { CommonModal } from 'src/components/shared/CommonModal'
import SuccessImage from 'src/assets/images/success.png'
import FailedImage from 'src/assets/images/failed.png'
import { ContractorPortalVerifyPhoneModal } from 'src/components/contractor-portal/contractor-portal-verify-phone-modal/ContractorPortalVerifyPhoneModal'
import { ContractorPortalBankPaymentMethodForm } from 'src/components/contractor-portal/contractor-portal-bank-payment-method-form/ContractorPortalBankPaymentMethodForm'
import { ContractorPortalStitchPaymentMethodDisplay } from 'src/components/contractor-portal/contractor-portal-payment-method-display/ContractorPortalStitchPaymentMethodDisplay'
import { ContractorPortalUsBankPaymentMethodDisplay } from 'src/components/contractor-portal/contractor-portal-payment-method-display/ContractorPortalUsBankPaymentMethodDisplay'

const ContractorPortalPaymentPageComponent = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const driver = useSelector(getDriver)
  const currentStep = useSelector(getCurrentCollectDataStep)

  const [stitchToken, setStitchToken] = useState<string | null>(null)
  const [verificationToken, setVerificationToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false)
  const [isFailedModalOpen, setIsFailedModalOpen] = useState<boolean>(false)
  const [paymentMethodInfo, setPaymentMethodInfo] = useState<
    ContractorPortalPaymentMethod | DriverPaymentMethod | null
  >(null)
  const [isVerifyPhoneModalOpen, setIsVerifyPhoneModalOpen] = useState<boolean>(false)
  const [isBankPaymentFormShown, setIsBankPaymentFormShown] = useState<boolean>(false)

  const openVerifyPhoneModal = useCallback(() => {
    setIsVerifyPhoneModalOpen(true)
  }, [])

  const closeVerifyPhoneModal = useCallback(() => {
    setIsVerifyPhoneModalOpen(false)
  }, [])

  const closeFailedModal = useCallback(() => {
    setIsFailedModalOpen(false)
  }, [])

  const closeSuccessModal = useCallback(() => {
    setIsSuccessModalOpen(false)
  }, [])

  const handleStitchCancel = useCallback(() => {
    setStitchToken(null)
  }, [])

  const handleBack = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.HOME_URL)
  }, [])

  const fetchPaymentMethod = useCallback(async () => {
    try {
      if (driver?.paymentProvider === PaymentProvider.UsBank) {
        const paymentMethod: DriverPaymentMethod = await createAsyncAction(
          dispatch,
          FetchPaymentMethod.request({ flow: 'contractorPortal' }),
        )
        setPaymentMethodInfo(paymentMethod)
      } else {
        const paymentMethod: ContractorPortalPaymentMethod = await createAsyncAction(
          dispatch,
          ContractorPortalFetchPaymentMethod.request(),
        )
        setPaymentMethodInfo(paymentMethod)
      }
    } catch (error) {
      showToast('Failed to fetch payment method', { variant: 'error' })
      setPaymentMethodInfo(null)
    }
  }, [driver?.paymentProvider, dispatch])

  const handleStitchComplete = useCallback(() => {
    setIsSuccessModalOpen(true)

    setTimeout(() => {
      fetchPaymentMethod()
      setStitchToken(null)
      setVerificationToken(null)
    }, 1000)
  }, [fetchPaymentMethod])

  const handleBankPaymentSuccess = useCallback(() => {
    setIsSuccessModalOpen(true)
    setIsBankPaymentFormShown(false)

    setTimeout(() => {
      fetchPaymentMethod()
      setVerificationToken(null)
    }, 1000)
  }, [fetchPaymentMethod])

  const handleStitchStart = useCallback(
    async (verificationToken: string) => {
      setIsLoading(true)

      try {
        const { token }: { token: string } = await createAsyncAction(
          dispatch,
          StitchFetchToken.request({
            stepId: currentStep?.id,
            token: verificationToken,
          }),
        )

        if (token) {
          setStitchToken(token)
        } else {
          throw new Error('no token')
        }
      } catch (err) {
        showToast('Failed to load payment page, please try again later', {
          variant: 'error',
        })
      }

      setIsLoading(false)
    },
    [currentStep?.id, handleStitchComplete],
  )

  const handleSuccessVerifyPhone = useCallback(
    (token: string) => {
      setIsVerifyPhoneModalOpen(false)

      if (driver?.paymentProvider === PaymentProvider.UsBank) {
        setVerificationToken(token)
        setIsBankPaymentFormShown(true)
      } else {
        handleStitchStart(token)
      }
    },
    [handleStitchStart, driver?.paymentProvider],
  )

  useEffect(() => {
    const listener = (e: MessageEvent) => {
      // TODO: Once webhooks are ready, we should send a request to the server to make stitch payment step as in_progress
      if (
        e.data === StitchIframeMessage.AccountAdded ||
        e.data === StitchIframeMessage.ManualReview
      ) {
        handleStitchComplete()
      }
    }

    window.addEventListener('message', listener)

    return () => {
      window.removeEventListener('message', listener)
    }
  }, [handleStitchComplete])

  useEffect(() => {
    if (driver && driver.paymentProvider !== undefined) {
      fetchPaymentMethod()
    }
  }, [driver?.paymentProvider, fetchPaymentMethod])

  const iframeSource = useMemo(() => {
    if (!stitchToken) {
      return null
    }

    const query = {
      auth: stitchToken,
      email: driver?.email,
      phone: driver?.phone,
      first_name: driver?.firstName,
      last_name: driver?.lastName,
    }

    const queryString = qs.stringify(_.omitBy(query, _.isNil))

    return `${process.env.REACT_APP_STITCH_URL}/?${queryString}`
  }, [stitchToken, driver])

  if (!driver) {
    return <div />
  }

  const getPaymentMethodDescription = () => {
    if (paymentMethodInfo) {
      return 'To update your payment method, tap the button below.'
    }

    if (driver?.paymentProvider === PaymentProvider.UsBank) {
      return ''
    }

    return "To add your payment method, tap the button below. You'll be redirected to our partner Stitch to securely make the change."
  }

  const shouldShowUpdateButton = () => {
    if (isBankPaymentFormShown) {
      return false
    }

    if (driver?.paymentProvider === PaymentProvider.UsBank && !paymentMethodInfo) {
      return false
    }

    return true
  }

  return (
    <ContractorPortalSecureWrapper isHeaderShown={!iframeSource}>
      <div css={tw`h-full flex w-full flex-col justify-between px-4 pt-20 sm:pt-6 pb-12`}>
        <div>
          <Button
            css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
            color="light"
            size="medium"
            leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
            onClick={
              isBankPaymentFormShown
                ? () => {
                    setIsBankPaymentFormShown(false)
                    setVerificationToken(null)
                  }
                : handleBack
            }
          >
            Back
          </Button>
          {isBankPaymentFormShown ? (
            <div css={tw`mt-4`}>
              <ContractorPortalBankPaymentMethodForm
                handleFailure={() => setIsFailedModalOpen(true)}
                handleSuccess={handleBankPaymentSuccess}
                paymentMethodData={paymentMethodInfo as DriverPaymentMethod | null}
                verificationToken={verificationToken}
              />
            </div>
          ) : (
            <>
              <Typography css={tw`mb-2 mt-4`} variant="h2">
                {driver?.paymentProvider === PaymentProvider.UsBank ? 'Payment Method' : 'Payments'}
              </Typography>
              <Typography css={tw`mb-4 text-shades-400`}>
                {getPaymentMethodDescription()}
              </Typography>
              {!(driver?.paymentProvider === PaymentProvider.UsBank && !paymentMethodInfo) && (
                <InfoContainer>
                  Note: Your updated payment method will also apply to all connected partners (if
                  any).
                </InfoContainer>
              )}
              {driver?.paymentProvider === PaymentProvider.Stitch && (
                <ContractorPortalStitchPaymentMethodDisplay
                  paymentMethod={paymentMethodInfo as ContractorPortalPaymentMethod | null}
                />
              )}
              {driver?.paymentProvider === PaymentProvider.UsBank && (
                <ContractorPortalUsBankPaymentMethodDisplay
                  paymentMethod={paymentMethodInfo as DriverPaymentMethod | null}
                />
              )}
            </>
          )}
        </div>
        {iframeSource && (
          <div css={tw`fixed w-full h-full top-0 left-0 z-50`}>
            <iframe css={tw`w-full h-full border-0`} src={iframeSource} />
            <SvgIcon
              css={tw`absolute right-5 top-5 w-5 h-5 z-20 cursor-pointer`}
              onClick={handleStitchCancel}
            >
              <CancelMajor css={tw`text-shades-400`} />
            </SvgIcon>
          </div>
        )}
        {shouldShowUpdateButton() && (
          <Button
            loading={isLoading}
            className={tw`w-full h-[56px]`}
            onClick={openVerifyPhoneModal}
            disabled={isBankPaymentFormShown}
          >
            Update payment method
          </Button>
        )}
      </div>
      {isVerifyPhoneModalOpen && (
        <ContractorPortalVerifyPhoneModal
          onClose={closeVerifyPhoneModal}
          onSuccess={handleSuccessVerifyPhone}
        />
      )}
      <CommonModal
        isOpen={isSuccessModalOpen}
        className={tw`max-w-[400px]`}
        onClose={closeSuccessModal}
      >
        <img css={tw`mb-2 w-full`} src={SuccessImage} />
        <Typography css={tw`mb-2 text-center`} variant="h2">
          Payment Method Successfully Updated
        </Typography>
        <Typography css={tw`mb-6 text-shades-400 text-center`}>
          Your new payment method is now active and will be used for future transactions.
        </Typography>
      </CommonModal>
      <CommonModal
        isOpen={isFailedModalOpen}
        className={tw`max-w-[400px]`}
        onClose={closeFailedModal}
      >
        <img css={tw`mb-2 w-full`} src={FailedImage} />
        <Typography css={tw`mb-2 text-center`} variant="h2">
          Something Went Wrong
        </Typography>
        <Typography css={tw`mb-6 text-shades-400 text-center`}>
          Please try again later or contact support.
        </Typography>
      </CommonModal>
      <Footer
        className={tw`flex mt-auto items-center px-4 pt-2 pb-8 bg-shades-50 justify-center w-full`}
      />
    </ContractorPortalSecureWrapper>
  )
}

export const ContractorPortalPaymentPage = memo(ContractorPortalPaymentPageComponent)
